import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send, CheckCircle, Clock } from 'lucide-react';
import { FaFacebook, FaInstagram, FaWhatsapp, FaTiktok } from 'react-icons/fa';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    emailjs.send(
      'service_hwla9qb',
      'template_jbxggsq',
      formData,
      'yn-6c3NzM8bkJIH42'
    )
      .then((result) => {
        console.log('Correo enviado:', result.text);
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({ name: '', phone: '', email: '', message: '' });
        }, 3000);
      })
      .catch((error) => {
        console.error('Error al enviar:', error.text);
      });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Teléfono',
      info: '323 707 6868',
      link: 'tel:+573237076868'
    },
    { icon: Mail, title: 'Email', info: 'vidbienat@gmail.com', link: 'mailto:vidbienat@gmail.com' },
    { icon: MapPin, title: 'Dirección', info: 'Cra 78A # 48 - 87, Medellín, Colombia', link: 'https://www.google.com/maps/search/Cra+78A+%23+48+-+87,+Medell%C3%ADn,+Colombia' },
    { icon: Clock, title: "Horario de atención", info: "Lunes a Viernes 8:00 AM - 4:00 PM", link: null },
  ];

  const socialLinks = [
    { icon: FaFacebook, href: "http://facebook.com/Bienestaralnaturalmedellin", color: "bg-blue-600", name: "Facebook" },
    { icon: FaInstagram, href: "https://www.instagram.com/bienestaralnatural.medellin/", color: "bg-pink-500", name: "Instagram" },
    { icon: FaWhatsapp, href: `https://wa.me/573237076868?text=${encodeURIComponent("Hola, los vi en su website y quiero obtener más información sobre sus servicios")}`, color: "bg-green-500", name: "WhatsApp" },
    { icon: FaTiktok, href: "https://www.tiktok.com/@bienestar.al.natu2?is_from_webapp=1&sender_device=pc", color: "bg-black", name: "TikTok" }
  ];

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
          <h2 className="text-4xl lg:text-5xl font-bold text-green-600 mb-6">Contáctanos</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Agenda tu cita o contáctanos para recibir orientación personalizada. Estaremos felices de acompañarte en tu proceso de equilibrio y renovación.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Información de contacto */}
          <motion.div className="space-y-8" initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Información de Contacto</h3>
              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <motion.a key={index} href={item.link} target={item.title === 'Dirección' ? "_blank" : undefined} className="flex items-center gap-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-200 hover:shadow-md transition-all duration-300" whileHover={{ scale: 1.02, x: 5 }}>
                    <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-3 rounded-xl">
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">{item.title}</h4>
                      <p className="text-gray-600">{item.info}</p>
                    </div>
                  </motion.a>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Síguenos en Redes Sociales @vidbienat</h3>
              <div className="grid grid-cols-3 gap-4">
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <motion.a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center justify-center gap-3 p-4 ${social.color} text-white rounded-2xl hover:shadow-lg transition-all duration-300`}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Icon className="w-6 h-6" />
                      <span className="font-semibold text-sm">{social.name}</span>
                    </motion.a>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Formulario de contacto */}
          <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
            <div className="bg-gradient-to-br from-gray-50 to-green-50 p-8 rounded-3xl border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Envíanos un Mensaje</h3>

              {isSubmitted ? (
                <motion.div className="text-center py-8" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.5 }}>
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h4 className="text-xl font-semibold text-green-600 mb-2">¡Mensaje Enviado!</h4>
                  <p className="text-gray-600">Te contactaremos pronto</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-gray-700 font-medium mb-2">Nombre Completo *</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300" placeholder="Tu nombre completo" />
                  </div>

                  {/* Campo de teléfono que abre WhatsApp al hacer clic */}
                  <div>
                    <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">Número de Teléfono *</label>
                    <input
                      type="text"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300"
                      placeholder="Tu número de teléfono"
                    />
                  </div>


                  <div>
                    <label htmlFor="email" className="block text-gray-700 font-medium mb-2">Correo Electrónico *</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300" placeholder="tu@email.com" />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-gray-700 font-medium mb-2">Mensaje (Opcional)</label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows="4" className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300 resize-none" placeholder="Cuéntanos cómo podemos ayudarte..."></textarea>
                  </div>

                  <motion.button type="submit" className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2" whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.98 }}>
                    <Send className="w-5 h-5" />
                    Enviar Mensaje
                  </motion.button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;



